import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, Pause, Check, Heart, Moon, Wind, Headphones, Video, UserRound } from 'lucide-react';
import { SessionVideoModal } from './SessionVideoModal';
import { useAudio } from '../../context/AudioContext';
import { mediaUrl, fmtMinutes } from '../../lib/programsApi';

const SERIF = { fontFamily: "'Playfair Display', serif" };

export const StepShell = ({ icon: Icon, label, done, children, testId }) => (
  <div className={`rounded-3xl border p-5 transition-colors duration-300 ${done ? 'bg-[#f9f4ea] border-[#7a9b8e]/40' : 'bg-[#FFFCF3] border-[#e3d2a8]/50'}`} data-testid={testId}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2 text-[#7a9b8e]">
        <Icon className="w-4 h-4" />
        <p className="text-[10px] uppercase tracking-[0.25em]">{label}</p>
      </div>
      {done && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#5c8477]" data-testid={`${testId}-done`}><Check className="w-3.5 h-3.5" /> Done</span>}
    </div>
    {children}
  </div>
);

const PrimaryBtn = ({ children, onClick, disabled, testId, subtle }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`w-full h-11 rounded-full text-xs font-semibold uppercase tracking-[0.18em] transition-[background-color,opacity,transform] duration-300 active:scale-[0.98] disabled:opacity-50 ${subtle ? 'bg-transparent text-[#7a9b8e] hover:text-[#1e3a5f]' : 'bg-[#0f1e33] text-[#f9f4ea] hover:bg-[#1e3a5f]'}`}>
    {children}
  </button>
);

// Visitors can do every step; only saving progress needs a profile.
export const GuestTrackNote = ({ testId, label = 'Sign in to track your progress' }) => (
  <Link to="/auth" data-testid={testId} className="w-full h-11 rounded-full border border-dashed border-[#a88a4c]/50 text-[#a88a4c] text-[11px] font-semibold uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2 hover:bg-[#D6AD60]/10 transition-colors">
    <UserRound className="w-3.5 h-3.5" /> {label}
  </Link>
);

const usePlayer = (content, cover, extra) => {
  const { play, pause, resume, state } = useAudio();
  const isCurrent = content && state.current?.contentId === content.id;
  const playing = isCurrent && state.playing;
  const toggle = () => {
    if (!content?.audioUrl) return;
    if (playing) return pause();
    if (isCurrent) return resume();
    play({ src: mediaUrl(content.audioUrl), title: content.title, subtitle: content.creator || 'Rowdy Solomon', artwork: mediaUrl(cover, 800), contentId: content.id, sourceContext: 'program', ...extra });
  };
  return { playing, isCurrent, toggle, error: isCurrent ? state.error : null };
};

export const MeditationStep = ({ day, cover, progress, locked, guest, onComplete }) => {
  // `locked` gates progress actions only — listening and watching are open to everyone.
  const c = day.primary;
  const done = !!progress?.meditationComplete;
  const { playing, toggle, error } = usePlayer(c, cover, { kind: 'audio' });
  const [video, setVideo] = useState(false);
  return (
    <StepShell icon={Headphones} label="Primary Meditation" done={done} testId="step-meditation">
      <h3 className="text-xl font-bold text-[#0f1e33] leading-tight mb-1" style={SERIF}>{c?.title || day.title}</h3>
      <p className="text-xs text-[#7a9b8e] mb-4">{c ? `${c.creator || 'Rowdy Solomon'} · ${fmtMinutes(c.durationSeconds)}` : 'Audio coming soon'}</p>
      {day.description && <p className="text-sm text-[#2d4a70] leading-relaxed mb-4">{day.description}</p>}
      {error && <p className="text-xs text-rose-600 mb-2" data-testid="step-meditation-error">We couldn't load this audio right now. You can still mark it complete below.</p>}
      <div className="flex flex-col gap-2">
        <button type="button" onClick={toggle} disabled={!c?.audioUrl} data-testid="step-meditation-play"
          className="w-full h-12 rounded-full bg-[#D6AD60] text-[#0f1e33] font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#c9a866] transition-colors duration-300 disabled:opacity-50">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? 'Pause' : 'Play meditation'}
        </button>
        {c?.videoUrl && (
          <button type="button" onClick={() => setVideo(true)} data-testid="step-meditation-watch-video"
            className="w-full h-11 rounded-full border border-[#0f1e33]/20 text-[#0f1e33] text-xs font-semibold uppercase tracking-[0.18em] inline-flex items-center justify-center gap-2 hover:bg-[#0f1e33]/5 transition-colors disabled:opacity-50">
            <Video className="w-4 h-4" /> Watch the Video
          </button>
        )}
        {c?.videoUrl && <SessionVideoModal open={video} content={c} onClose={() => setVideo(false)} onEnded={() => { if (!done && !locked) onComplete('video_end'); }} />}
        {done ? (
          <p className="text-center text-xs font-semibold text-[#5c8477] py-2" data-testid="step-meditation-completed">✓ Meditation completed{progress?.meditationMethod === 'audio_end' ? ' · listened to the end' : progress?.meditationMethod === 'video_end' ? ' · watched to the end' : ''}</p>
        ) : guest ? (
          <GuestTrackNote testId="step-meditation-guest-track" />
        ) : (
          <PrimaryBtn subtle onClick={() => onComplete('manual')} disabled={locked} testId="step-meditation-finish">I've finished this meditation</PrimaryBtn>
        )}
      </div>
    </StepShell>
  );
};

export const AffirmationStep = ({ day, progress, locked, guest, onComplete }) => {
  const done = !!progress?.affirmationComplete;
  return (
    <StepShell icon={Heart} label="Daily Affirmation" done={done} testId="step-affirmation">
      <p className="text-2xl text-[#0f1e33] leading-snug mb-5 italic" style={SERIF} data-testid="step-affirmation-text">“{day.affirmationText}”</p>
      {done ? (
        <p className="text-xs font-semibold text-[#5c8477]" data-testid="step-affirmation-completed">✓ Carried with you today</p>
      ) : guest ? (
        <GuestTrackNote testId="step-affirmation-guest-track" label="Sign in to carry this with you" />
      ) : (
        <PrimaryBtn onClick={onComplete} disabled={locked} testId="step-affirmation-complete"><span className="inline-flex items-center gap-2"><Heart className="w-3.5 h-3.5" /> I'll carry this with me</span></PrimaryBtn>
      )}
    </StepShell>
  );
};

export const BreathingStep = ({ day }) => {
  const navigate = useNavigate();
  if (!day.breathing) return null;
  return (
    <StepShell icon={Wind} label="Breathing Exercise" testId="step-breathing">
      <p className="text-lg font-bold text-[#0f1e33] mb-3" style={SERIF}>{day.breathing.title}</p>
      <PrimaryBtn onClick={() => navigate('/breathing')} testId="step-breathing-open">Open breathing</PrimaryBtn>
    </StepShell>
  );
};

export const EveningStep = ({ day, cover, progress, locked, guest, onComplete, onSkip }) => {
  const c = day.evening;
  const done = !!progress?.eveningComplete;
  const skipped = !!progress?.eveningSkipped && !done;
  const { playing, toggle, error } = usePlayer(c, c?.coverImageUrl || cover, { kind: c?.type === 'soundscape' ? 'soundscape' : 'audio', loop: c?.type === 'soundscape' });
  return (
    <StepShell icon={Moon} label="Tonight" done={done} testId="step-evening">
      <h3 className="text-xl font-bold text-[#0f1e33] leading-tight mb-1" style={SERIF} data-testid="step-evening-title">{c ? `${c.title} · ${fmtMinutes(c.durationSeconds)}` : 'Rest well tonight'}</h3>
      <p className="text-xs text-[#7a9b8e] mb-4">{c?.type === 'soundscape' ? 'Soundscape · loops softly until you sleep' : c ? 'Sleep meditation' : 'No evening track for tonight'}</p>
      {error && <p className="text-xs text-rose-600 mb-2">We couldn't load this audio right now.</p>}
      {c && (
        <button type="button" onClick={toggle} data-testid="step-evening-play"
          className="w-full h-12 rounded-full bg-[#1e3a5f] text-[#f9f4ea] font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#2d4a70] transition-colors duration-300 disabled:opacity-50 mb-2">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} {playing ? 'Pause' : 'Play tonight'}
        </button>
      )}
      {done ? (
        <p className="text-center text-xs font-semibold text-[#5c8477] py-2" data-testid="step-evening-completed">✓ Evening complete</p>
      ) : skipped ? (
        <p className="text-center text-xs text-[#7a9b8e] py-2" data-testid="step-evening-skipped">Skipped for tonight — you can still listen anytime.</p>
      ) : guest ? (
        <GuestTrackNote testId="step-evening-guest-track" />
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <PrimaryBtn subtle onClick={onSkip} disabled={locked} testId="step-evening-skip">Skip for tonight</PrimaryBtn>
          <PrimaryBtn onClick={onComplete} disabled={locked} testId="step-evening-complete">Mark as listened</PrimaryBtn>
        </div>
      )}
    </StepShell>
  );
};
